import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { TopHeaderService } from 'src/app/core/services/top-header/top-header.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { LocalStorageKeys } from 'src/app/shared/models/constants/storage.keys';

@Component({
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
})
/**
 * Home page
 */
export class HomePage implements OnInit, OnDestroy {
  private subsink = new Subscription();
  isMobile = true;
  labels = {
    ok: 'ok',
    equipmentNotFoundDialogContent: 'business-has-no-equipment-dialog-content',
    title500Points: 'title-confirmation-modal-points',
    content500Points: 'confirmation-modal-points',
    unificationScheduledTitle: 'unification-scheduled-title',
    unificationScheduledContent: 'unification-scheduled-content',
    unificationFinishedTitle: 'unification-finished-title',
    unificationFinishedContent: 'unification-finished-content',
  };
  loading = false;

  /**
   * @constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    private breakpointObserver: BreakpointObserver,
    private readonly dialogService: DialogService,
    public readonly topHeaderService: TopHeaderService,
    public readonly authenticatedUserService: AuthenticatedUserService,
    private readonly userService: UserService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);

    if (
      this.authenticatedUserService.representative == null ||
      this.topHeaderService.business == null ||
      this.topHeaderService.pointSummary == null
    ) {
      this.loading = true;
    }
    this.topHeaderService.fetchTopHeaderInfo().finally(() => {
      this.loading = false;
      if (this.topHeaderService.business?.newBusiness) {
        this.dialogService.openDialog(
          {
            title: this.labels.title500Points,
            content: this.labels.content500Points,
            successButtonText: this.labels.ok,
          },
          () => {
            this.callCheckNewBusiness();
          },
          () => {
            this.callCheckNewBusiness();
          },
          450
        );
      }
      this.checkUnificationNotifications();
    });

    this.subsink.add(
      this.breakpointObserver
        .observe(['(min-width: 620px)'])
        .subscribe((state: BreakpointState) => {
          this.isMobile = !state.matches;
        })
    );
  }

  /**
   * Call check new business endpoint
   */
  private callCheckNewBusiness() {
    this.userService.checkNewBusiness().subscribe((_e) => {
      this.topHeaderService.business!.newBusiness = false;
    });
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Checks for pending merge notifications to display.
   */
  private checkUnificationNotifications() {
    const currentBusinessId = this.topHeaderService.business?.id;
    if (!currentBusinessId || this.getNotifiedUnificationLoginFromStorage()) {
      return;
    }

    this.userService.getBusinessUnificationStatus(currentBusinessId).subscribe({
      next: (unification) => {
        if (!unification) {
          return;
        }

        if (!unification.notified) {
          if (
            unification.status === 'PENDING' ||
            unification.status === 'SCHEDULED'
          ) {
            this.showUnificationDialog(
              this.labels.unificationScheduledTitle,
              this.labels.unificationScheduledContent,
              currentBusinessId
            );
            this.setNotifiedUnificationLoginFromStorage();
          } else if (unification.status === 'UNIFIED') {
            this.showUnificationDialog(
              this.labels.unificationFinishedTitle,
              this.labels.unificationFinishedContent,
              currentBusinessId
            );
            this.setNotifiedUnificationLoginFromStorage();
          }
        }
      },
    });
  }

  /**
   * @param {string} title
   * @param {string} content
   * @param {number} businessId
   */
  private showUnificationDialog(
    title: string,
    content: string,
    businessId: number
  ) {
    this.dialogService.openDialog(
      {
        title: title,
        content: content,
        successButtonText: this.labels.ok,
      },
      () => this.markUnificationAsNotified(businessId),
      undefined,
      450
    );
  }

  /**
   * Gets Unification notified on login value
   * @return {boolean} Unification notified on login value
   */
  private getNotifiedUnificationLoginFromStorage(): boolean {
    const item = localStorage.getItem(LocalStorageKeys.NOTIFIED_ON_LOGIN)!;
    if (!item) {
      return false;
    }
    return item.toLowerCase() === 'true';
  }

  /**
   * Set Unification notified on login value to true on local storage
   */
  private setNotifiedUnificationLoginFromStorage(): void {
    localStorage.setItem(
      LocalStorageKeys.NOTIFIED_ON_LOGIN,
      JSON.stringify(true)
    );
  }

  /**
   * Mark a business as notified of unification
   * @param {number} businessId
   */
  private markUnificationAsNotified(businessId: number) {
    this.userService
      .marksBusinessAsNotifiedAboutUnification(businessId)
      .subscribe();
  }
}
